import { ClipboardCheck, Scale } from 'lucide-react'
import { useId } from 'react'

import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import type { Pesewas } from '@/lib/money'

import { COLUMN_SHORT_LABELS, type CollectionColumn } from './constants'
import type { SheetTotals } from './sheet-model'

/**
 * The running total, against the total the officer declared.
 *
 * This is the control that makes the screen worth having. The paper sheet was added up in the
 * field and the officer wrote a figure at the bottom; the system adds up what was keyed. The two
 * agreeing is the evidence that forty amounts were transcribed correctly, and it is available
 * *before* anything posts — which is the only moment it is cheap to act on.
 *
 * A difference is not silently permitted and it is not silently blocked either. It has to be
 * acknowledged deliberately, with the screen saying plainly what happens next: nothing in this
 * system records a transcription variance, so a difference is a paper exception and has to be
 * raised as one. Pretending otherwise would leave a figure nobody ever looks at again.
 */
export function SheetTotalsBar({
  totals,
  columns,
  declaredMinor,
  onDeclaredChange,
  varianceAcknowledged,
  onVarianceAcknowledgedChange,
  canPost,
  blockedReason,
  isPosting,
  onReview,
}: {
  totals: SheetTotals
  columns: CollectionColumn[]
  declaredMinor: Pesewas | null
  onDeclaredChange: (value: Pesewas | null) => void
  varianceAcknowledged: boolean
  onVarianceAcknowledgedChange: (value: boolean) => void
  canPost: boolean
  /** Why the button is disabled, in one line. Null when it is not. */
  blockedReason: string | null
  isPosting: boolean
  onReview: () => void
}) {
  const acknowledgeId = useId()
  const varianceMinor = declaredMinor === null ? null : declaredMinor - totals.capturedMinor
  const balanced = varianceMinor === 0

  return (
    <Card className="sticky bottom-0 z-10 shadow-lg">
      <CardContent className="flex flex-wrap items-end gap-x-6 gap-y-3 p-3">
        <dl className="flex min-w-0 flex-wrap gap-x-5 gap-y-1">
          {columns.map((column) => (
            <div key={column}>
              <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                {COLUMN_SHORT_LABELS[column]}
              </dt>
              <dd>
                <MoneyDisplay value={totals.byColumn[column]} className="text-sm" />
              </dd>
            </div>
          ))}

          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
              Captured
            </dt>
            <dd>
              <MoneyDisplay value={totals.capturedMinor} emphasis="strong" className="text-base" />
            </dd>
          </div>

          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Lines</dt>
            <dd className="numeric text-base font-semibold text-heading tabular-nums">
              {totals.lineCount}
              <span className="ml-1 text-2xs font-normal text-muted">
                across {totals.memberCount} member{totals.memberCount === 1 ? '' : 's'}
              </span>
            </dd>
          </div>
        </dl>

        <div className="w-48 space-y-1">
          <Label htmlFor="declared-total" className="text-2xs tracking-wide uppercase">
            Officer&apos;s declared total
          </Label>
          <MoneyInput
            id="declared-total"
            value={declaredMinor}
            onChange={onDeclaredChange}
            disabled={isPosting}
            aria-invalid={varianceMinor !== null && !balanced}
          />
        </div>

        <div className="min-w-40 space-y-1">
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Difference</p>
          {varianceMinor === null ? (
            <p className="text-sm text-muted">Not declared</p>
          ) : (
            <div className="flex items-center gap-1.5">
              <Scale
                className={balanced ? 'size-4 text-success-text' : 'size-4 text-danger-text'}
                aria-hidden
              />
              <MoneyDisplay
                value={varianceMinor}
                signed
                colourNegative
                emphasis="strong"
                className={balanced ? 'text-success-text' : undefined}
              />
            </div>
          )}
        </div>

        <div className="ml-auto flex flex-col items-end gap-2">
          {varianceMinor !== null && !balanced ? (
            <div className="flex max-w-md items-start gap-2">
              <Checkbox
                id={acknowledgeId}
                checked={varianceAcknowledged}
                onCheckedChange={(checked) => onVarianceAcknowledgedChange(checked === true)}
                disabled={isPosting}
                className="mt-0.5"
              />
              <Label htmlFor={acknowledgeId} className="text-xs font-normal text-body">
                The declared total and the keyed total differ. Nothing in this system records that
                difference — raise it as a paper exception with the officer before posting.
              </Label>
            </div>
          ) : null}

          {blockedReason ? <p className="text-xs text-muted">{blockedReason}</p> : null}

          <Button variant="primary" onClick={onReview} disabled={!canPost || isPosting}>
            <ClipboardCheck />
            {isPosting ? 'Posting…' : 'Review and post'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
